import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";

import { CartContext } from "../context/CartContext";
import { EventContext } from "../context/EventContext";
import { AuthContext } from "../context/AuthContext";
import { useForm, Controller, FormProvider } from "react-hook-form";

import countriesObject from "../countryCodes";
import statesObject from "../stateCodes";
import Header from "./Header";
import Cart from "./Cart";
import CartSummary from "./CartSummary";

const Checkout = () => {
  useEffect(() => {
    document.body.className = "checkout dropShip";
    return () => {
      document.body.className = "";
    };
  }, []);

  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const { user, setUser } = useContext(AuthContext);
  const { customer, eventID, setAddressUser } = useContext(EventContext);
  const { cartRef, addressRef, submitOrder, sortingPath, dropShip } =
    useContext(CartContext);
  const form = useForm();

  // Saves the user's address
  const save = useCallback(() => {
    // Only save the address if it's the user's drop ship address
    if (dropShip) {
      // Convert address to uppercase to avoid problems with Jesta
      const uppercaseAddress = Object.fromEntries(
        Object.entries(form.getValues()).map(([k, v]) =>
          typeof v === "string" ? [k, v.toUpperCase()] : [k, v]
        )
      );
      
      // Set the user's address
      setUser((user) => {
        return {
          ...user,
          address: uppercaseAddress,
        };
      });
      setAddressUser(
       uppercaseAddress
      )
    } else {
      // Set the user's name
      const values = form.getValues();
      setUser((user) => {
        return {
          ...user,
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          address: {
            ...user.address,
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNumber: values.phoneNumber,
          },
        };
      });
      
    }
  }, [dropShip, form.getValues, setUser]);

  // Set the user's address if they have it saved, else use customer address
  useEffect(() => {
    if (!user || !customer || !customer.name) return null;

    const {
      firstName,
      lastName,
      phoneNumber,
      street,
      street2,
      city,
      state,
      zip,
      country,
    } = user.address || {};

    form.setValue("firstName", firstName);
    form.setValue("lastName", lastName);
    form.setValue("phoneNumber", phoneNumber);
    form.setValue("clubName", customer.name);
    form.setValue("street", !!dropShip ? street : customer.street);
    form.setValue("street2", !!dropShip ? street2 : customer.street2);
    form.setValue("city", !!dropShip ? city : customer.city);
    form.setValue("state", !!dropShip ? state : customer.state);
    form.setValue("zip", !!dropShip ? zip : customer.zip);
    form.setValue("country", !!dropShip ? country : customer.countryCode);
  }, [dropShip, user, customer, form.setValue]);

  const populateCountries = useCallback(async () => {
    try {
      setStateOptions(
        statesObject.map((state) => {
          return { value: state.code, label: state.name };
        })
      );
      setCountryOptions(
        countriesObject.map((country) => {
          return { value: country.code, label: country.name };
        })
      );
    } catch (err) {
      console.log(`Error fetching countries: ${err}`);
    }
  }, []);

  // Populate the list of countries
  useEffect(() => {
    populateCountries();
  }, [populateCountries]);

  const renderForm = useCallback(() => {
    return (
      <form
        onSubmit={form.handleSubmit(submitOrder)}
        ref={addressRef}
        className="shippingForm"
      >
        <div className="shippingFormHeader">
          <h2>{dropShip ? "Shipping Address" : "Shipping Information"}</h2>
        </div>
        <div className="shippingFormBody">
          <div className="shippingFormBodyRow">
            <label>First Name</label>
            <input
              style={{ textTransform: "uppercase" }}
              pattern="[a-zA-Z]*"
              {...form.register("firstName", { required: true })}
              className={form.formState.errors.firstName ? "invalid-field" : ""}
              onBlur={() => save()}
              placeholder="First Name"
              disabled={false}
              type="text"
            />
            {form.formState.errors.firstName && (
              <b className="error">First name is required.</b>
            )}
          </div>
          <div className="shippingFormBodyRow">
            <label>Last Name</label>
            <input
              style={{ textTransform: "uppercase" }}
              pattern="[a-zA-Z]*"
              {...form.register("lastName", { required: true })}
              className={form.formState.errors.lastName ? "invalid-field" : ""}
              onBlur={() => save()}
              placeholder="Last Name"
              disabled={false}
              type="text"
            />
            {form.formState.errors.lastName && <b className="error">Last name is required.</b>}
          </div>
          <div className="shippingFormBodyRow">
            <label>Phone Number</label>
            <PhoneInput
              defaultCountry="US"
              {...form.register("phoneNumber", { required: false })}
              onBlur={() => save()}
              onChange={(number) => form.setValue("phoneNumber", number)}
              placeholder="Phone Number"
              disabled={false}
            />
          </div>
          {!dropShip && (
            <div className="shippingFormBodyRow">
              <label>Club Name</label>
              <input
                style={{ textTransform: "uppercase" }}
                pattern="[a-zA-Z]*"
                {...form.register("clubName")}
                className={form.formState.errors.clubName ? "invalid-field" : ""}
                onBlur={() => save()}
                placeholder="Club Name"
                disabled={!dropShip}
                type="text"
              />
            </div>
          )}
          <div className="shippingFormBodyRow">
            <label>Street</label>
            <input
              style={{ textTransform: "uppercase" }}
              pattern="[a-zA-Z]*"
              {...form.register("street", { required: dropShip })}
              className={form.formState.errors.street ? "invalid-field" : ""}
              onBlur={() => save()}
              placeholder="Address Line 1"
              disabled={!dropShip}
              type="text"
            />
            {dropShip && form.formState.errors.street && (
              <b className="error">Street is required.</b>
            )}
          </div>
          <div className="shippingFormBodyRow">
            <input
              style={{ textTransform: "uppercase" }}
              pattern="[a-zA-Z]*"
              {...form.register("street2", { required: false })}
              className={form.formState.errors.street2 ? "invalid-field" : ""}
              onBlur={() => save()}
              placeholder="Address Line 2"
              disabled={!dropShip}
              type="text"
            />
          </div>
          <div className="shippingFormBodyRow">
            <label>City</label>
            <input
              style={{ textTransform: "uppercase" }}
              pattern="[a-zA-Z]*"
              {...form.register("city", { required: dropShip })}
              className={form.formState.errors.city ? "invalid-field" : ""}
              onBlur={() => save()}
              placeholder="City"
              disabled={!dropShip}
              type="text"
            />
            {dropShip && form.formState.errors.city && (
              <b className="error">City is required.</b>
            )}
          </div>
          <div className="shippingFormBodyRow">
            <label>State</label>
            <Controller
              name="state"
              control={form.control}
              render={({ onChange, value, name, ref }) => (
                <Select
                  className="react-select"
                  classNamePrefix={form.formState.errors.state ? "invalid-field" : ""}
                  {...form.register("state", { required: dropShip })}
                  inputRef={ref}
                  isDisabled={!dropShip}
                  options={stateOptions}
                  value={stateOptions.find(
                    (c) => c.value === form.getValues()["state"]
                  )}
                  onChange={(state) => {
                    form.setValue("state", state.value);
                    save(state);
                  }}
                />
              )}
            />
            {dropShip && form.formState.errors.state && (
              <b className="error">State is required.</b>
            )}
          </div>
          <div className="shippingFormBodyRow">
            <label>Zip Code</label>
            <input
              pattern="[0-9]*"
              {...form.register("zip", { required: dropShip })}
              className={form.formState.errors.zip ? "invalid-field" : ""}
              onBlur={() => save()}
              placeholder="Zip"
              disabled={!dropShip}
              type="text"
            />
            {dropShip && form.formState.errors.zip && (
              <b className="error">Zip code is required.</b>
            )}
          </div>
          <div className="shippingFormBodyRow">
            <label>Country</label>
            <Controller
              name="country"
              control={form.control}
              render={({ onChange, value, name, ref }) => (
                <Select
                  className="react-select"
                  classNamePrefix={form.formState.errors.country ? "invalid-field" : ""}
                  {...form.register("country", { required: dropShip })}
                  inputRef={ref}
                  isDisabled={!dropShip}
                  options={countryOptions}
                  value={countryOptions.find(
                    (c) => c.value === form.getValues()["country"]
                  )}
                  onChange={(country) => {
                    form.setValue("country", country.value);
                    save(country);
                  }}
                />
              )}
            />
            {dropShip && form.formState.errors.country && (
              <b className="error">Country is required.</b>
            )}
          </div>
        </div>
      </form>
    );
  }, [
    dropShip,
    addressRef,
    submitOrder,
    stateOptions,
    countryOptions,
    save,
    form
  ]);

  return (
    <>
      <Header />
      <div className="productPane">
        <FormProvider {...form}>

        <div ref={cartRef} className="checkoutGrid">
          <div>
            <Link
              to={!!sortingPath ? sortingPath : `/${eventID}/store`}
              className="backLink iconArrow"
            >
              <span className="iconArrow" />
              Back to Shopping
            </Link>
            <Cart />
            {renderForm()}
          </div>
          <div className="summary">
            <div className="summaryWrapper">
              <CartSummary user={user} watch={form.watch} trigger={form.trigger} />
            </div>
          </div>
        </div>
        </FormProvider>
      </div>
    </>
  );
};

export default Checkout;
